import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { FormikDatePicker } from 'src/components/shared/form/formik/FormikDatePicker'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { PERSON_IDENTIFICATION_TYPE_OPTIONS } from 'src/constants/payments-ops/bankOnboarding'
import type { BeneficialOwnerFormValues } from 'src/interfaces/bankOnboarding'
import { OnboardingAddressFields } from './OnboardingAddressFields'
import { beneficialOwnerValidator } from './onboardingSteps.validator'

interface Props {
  /** `null` closes the modal. */
  owner: BeneficialOwnerFormValues | null
  isNew: boolean
  /** While true the modal cannot be dismissed or saved — the start request is in flight. */
  isLocked?: boolean
  onSave(owner: BeneficialOwnerFormValues): void
  onClose(): void
}

const FORM_ID = 'beneficial-owner-form'

const OWNER_ADDRESS_FIELDS = {
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  state: 'state',
  zipCode: 'zipCode',
  country: 'country',
}

/** A separate FLAT Formik form per owner — the wizard form cannot hold nested paths. */
const BeneficialOwnerModalComponent = ({
  owner,
  isNew,
  isLocked = false,
  onSave,
  onClose,
}: Props) => {
  const handleClose = useCallback(() => {
    if (isLocked) {
      return
    }

    onClose()
  }, [isLocked, onClose])

  if (!owner) {
    return null
  }

  return (
    <Formik<BeneficialOwnerFormValues>
      initialValues={owner}
      validationSchema={beneficialOwnerValidator}
      onSubmit={(values) => {
        onSave(values)
      }}
    >
      <CommonModal
        isOpen
        title={isNew ? 'Add beneficial owner' : 'Edit beneficial owner'}
        className={tw`max-w-[640px]`}
        onClose={handleClose}
        buttons={
          <Fragment>
            <Button size="small" color="grey" disabled={isLocked} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="small"
              color="dark"
              type="submit"
              form={FORM_ID}
              disabled={isLocked}
              data-testid="onboarding-save-owner"
            >
              Save owner
            </Button>
          </Fragment>
        }
      >
        <Form id={FORM_ID} css={tw`grid grid-cols-2 gap-4`}>
          <FormikTextField<BeneficialOwnerFormValues>
            name="firstName"
            label="First name *"
            placeholder="First name"
          />
          <FormikTextField<BeneficialOwnerFormValues>
            name="lastName"
            label="Last name *"
            placeholder="Last name"
          />
          <FormikTextField<BeneficialOwnerFormValues>
            name="ownershipPercentage"
            label="Ownership % *"
            placeholder="0–100"
            inputProps={{ inputMode: 'decimal' }}
          />
          <FormikDatePicker<BeneficialOwnerFormValues>
            name="dateOfBirth"
            label="Date of birth *"
            fullWidth
            validateOnPick
          />
          <FormikSelect<BeneficialOwnerFormValues>
            name="identificationType"
            label="ID type *"
            options={PERSON_IDENTIFICATION_TYPE_OPTIONS}
            placeholder="SSN or ITIN"
          />
          <FormikTextField<BeneficialOwnerFormValues>
            name="identificationNumber"
            label="ID number *"
            placeholder="9 digits"
          />
          <FormikTextField<BeneficialOwnerFormValues>
            name="identificationIssuingCountry"
            label="ID issuing country *"
            placeholder="US"
            inputProps={{ maxLength: 2 }}
          />
          <FormikTextField<BeneficialOwnerFormValues>
            name="phoneNumber"
            label="Phone *"
            placeholder="10 digits"
            inputProps={{ inputMode: 'tel' }}
          />
          <div css={tw`col-span-2`}>
            <OnboardingAddressFields fieldNames={OWNER_ADDRESS_FIELDS} />
          </div>
        </Form>
      </CommonModal>
    </Formik>
  )
}

export const BeneficialOwnerModal = memo(BeneficialOwnerModalComponent)
